import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { ContactService } from "../../contact.service";
import { GetQualifiedContactListRequestDTO } from "../../dtos/qualified-contact-list-request.dto";
import { debounceTime, Subject, takeUntil } from "rxjs";
import { FormBuilder, FormGroup } from "@angular/forms";
import { GetQualifiedContactListResponseDTO } from "../../dtos/qualified-contact-list-response.dto";
import { QualifiedContactList } from "../../models/qualified-list.model";
import { PaginationDto } from "src/app/core/dto/pagination.dto";
import { DetailedListContact } from "../../models/detailed-list.model";
import { DetailedListClientComponent } from "../detailed-list/components/detailed-list-client/detailed-list-client.component";
import { ActivatedRoute } from "@angular/router";
import { NgbOffcanvas } from "@ng-bootstrap/ng-bootstrap";
import { GetDetailedListRequestDTO } from "../../dtos/detailed-list-request.dto";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { DetailedQualifiedListComponent } from "./components/detailed-qualified-list/detailed-qualified-list.component";
import { ErrorMessagesService } from "src/app/shared/messages/services/error-messages.service";
import { AuthService } from "src/app/core/services/auth/auth.service";
import { StatusConfigService } from "src/app/pages/call-center/pages/status-config/services/status-config.service";
import { StatusCliente } from "src/app/pages/call-center/pages/status-config/model/status.model";
import { qualifiedListTourSteps } from "./qualified-list.tour";

@Component({
  selector: "app-qualified-list",
  templateUrl: "./qualified-list.component.html",
  styleUrl: "./qualified-list.component.scss",
})
export class QualifiedListComponent implements OnInit {
  private readonly destroy$ = new Subject<void>();
  private readonly destroyedRef = inject(DestroyRef);
  rows: DetailedListContact[] = [];

  breadCrumbItems!: Array<{}>;
  isLoading = false;
  error = "";
  lists: QualifiedContactList[] = [];
  filtro?: GetQualifiedContactListRequestDTO | null;

  pagination: PaginationDto = {
    paginaAtual: 1,
    quantidadePorPagina: 10,
    totalDeItens: 0,
    ultimaPagina: 1,
  };
  filterForm: FormGroup;

  erro403 = false;

  //permissao
  podeEditar = false;

  // Status select list
  statusMessages: StatusCliente[] = [];
  statusFiltrados: StatusCliente[] = [];
  buscaStatus = '';
  dropdownStatusAberto = false;
  statusSelecionado: StatusCliente | null = null;

  constructor(
    private contactService: ContactService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private offcanvasService: NgbOffcanvas,
    private errorMessagesService: ErrorMessagesService,
    private authService: AuthService,
    private statusConfigService: StatusConfigService
  ) {
    this.filterForm = this.fb.group({
      "Paginacao.PaginaAtual": [1],
      "Paginacao.QuantidadePorPagina": [10],
      Cliente: [""],
      Feiras: [""],
      Status: [""],
      ProximoContato: [""],
      Contato: [""],
    });
  }

  ngOnInit(): void {
    this.breadCrumbItems = [
      { label: "Contatos" },
      { label: "Qualificados", active: true },
    ];

    this.permissaoAdicionarUsuario();
    this.carregarStatus();

    this.getQualifiedContactList(this.mapearFiltrosParaDTO(this.filterForm.value));
    this.filterForm.valueChanges
      .pipe(debounceTime(400))
      .subscribe((filters) => {
        this.getQualifiedContactList(this.mapearFiltrosParaDTO(filters));
      })
  }

  carregarStatus(): void {
    this.statusConfigService.getStatusMessages()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (status) => {
          this.statusMessages = status;
          this.statusFiltrados = [...this.statusMessages];
        },
        error: (error) => {
          console.error('Erro ao carregar status:', error);
        }
      });
  }

  filtrarStatus(): void {
    if (!this.buscaStatus.trim()) {
      this.statusFiltrados = [...this.statusMessages];
      return;
    }
    
    const termo = this.buscaStatus.toLowerCase();
    this.statusFiltrados = this.statusMessages.filter(status => {
      // Extrair o nome do status (formato "nome|cor")
      const [nome] = status.nome.split('|');
      return nome.toLowerCase().includes(termo);
    });
  }

  abrirDropdownStatus(): void {
    this.dropdownStatusAberto = true;
    // Resetar o filtro para mostrar todas as opções quando abrir o dropdown
    this.statusFiltrados = [...this.statusMessages];
    // Limpar o campo de busca para permitir nova busca
    // Mas manter o texto se já houver um status selecionado (será limpo quando começar a digitar)
  }

  onInputStatus(): void {
    // Quando o usuário começar a digitar, limpar a seleção se houver
    if (this.statusSelecionado) {
      this.statusSelecionado = null;
      this.filterForm.patchValue({ Status: '' });
    }
    this.filtrarStatus();
  }

  fecharDropdownStatus(): void {
    setTimeout(() => {
      this.dropdownStatusAberto = false;
    }, 200);
  }

  selecionarStatus(status: StatusCliente): void {
    this.statusSelecionado = status;
    // Extrair o nome do status (formato "nome|cor")
    const [nome] = status.nome.split('|');
    this.buscaStatus = nome;
    // Atualizar o formulário com o ID do status selecionado
    this.filterForm.patchValue({ Status: status.id }, { emitEvent: true });
    this.dropdownStatusAberto = false;
    // Resetar o filtro para que todas as opções apareçam na próxima vez que abrir
    this.statusFiltrados = [...this.statusMessages];
    
    // Debug: log do status selecionado
    console.log('Status selecionado:', {
      id: status.id,
      nome: nome,
      statusCompleto: status
    });
  }

  getStatusNome(status: StatusCliente): string {
    // Extrair o nome do status (formato "nome|cor")
    const [nome] = status.nome.split('|');
    return nome;
  }

  limparStatus(): void {
    this.statusSelecionado = null;
    this.buscaStatus = '';
    this.statusFiltrados = [...this.statusMessages];
    this.filterForm.patchValue({ Status: '' });
  }

  mapearFiltrosParaDTO(formValue: any): GetQualifiedContactListRequestDTO {
    // Garantir que StatusId seja uma string válida ou undefined
    let statusId: string | undefined = undefined;
    if (formValue.Status) {
      const statusValue = String(formValue.Status).trim();
      if (statusValue && statusValue !== '' && statusValue !== 'null' && statusValue !== 'undefined') {
        statusId = statusValue;
      }
    }

    const dto: GetQualifiedContactListRequestDTO = {
      Cliente: formValue.Cliente?.trim() || undefined,
      StatusId: statusId,
      FeiraId: formValue.Feiras?.trim() || undefined,
      Agendamento: formValue.ProximoContato?.trim() || undefined,
      PaginaAtual: formValue["Paginacao.PaginaAtual"] || 1,
      QuantidadePorPagina: formValue["Paginacao.QuantidadePorPagina"] || 10,
    };

    // Remover campos undefined ou vazios
    Object.keys(dto).forEach(key => {
      const value = dto[key as keyof GetQualifiedContactListRequestDTO];
      if (value === undefined || value === '' || value === null) {
        delete dto[key as keyof GetQualifiedContactListRequestDTO];
      }
    });

    return dto;
  }

  getQualifiedContactList(filters: GetQualifiedContactListRequestDTO) {
    this.isLoading = true;

    this.contactService.getQualifiedContactList(filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: GetQualifiedContactListResponseDTO) => {
          this.lists = response.qualificados;
          this.pagination = response.paginacao;
          this.isLoading = false;
          this.filtro = filters;
        },
        error: (error) => {
          if (error.status == 403) {
            this.erro403 = true;
            return;
          } 
          this.error = this.errorMessagesService.getMessage('ERR_0011');
          console.error(error);
        }
      })

  }

  onPageChange(page: number) {
    this.filterForm.patchValue({
      "Paginacao.PaginaAtual": page,
    });
  }

  limparFiltros(): void {
    this.filterForm.patchValue({
      "Paginacao.PaginaAtual": 1,
      "Paginacao.QuantidadePorPagina": 10,
      Cliente: "",
      Feiras: "",
      Status: "",
      ProximoContato: "",
      Contato: "",
    });
    this.limparStatus();
  }

  getStatusBadgeClass(status: string): string {
    if (!status) return 'bg-secondary-subtle text-secondary';
    
    // O status vem no formato "nome|cor" onde cor pode ser "bg-success-subtle" ou similar
    // O pipe statusCorFormatter já extrai a cor, mas precisamos garantir o formato correto
    const [nome, cor] = status.split('|');
    
    if (cor) {
      // Se a cor já está no formato "bg-{cor}-subtle", extrair a cor base e adicionar text-{cor}
      const corMatch = cor.match(/bg-(\w+)-subtle/);
      if (corMatch && corMatch[1]) {
        const corBase = corMatch[1]; // ex: "success", "warning", "danger", etc.
        return `bg-${corBase}-subtle text-${corBase}`;
      }
      // Se não está no formato esperado, tentar mapear manualmente
      const corLower = cor.toLowerCase().trim();
      if (corLower.includes('success') || corLower.includes('verde')) {
        return 'bg-success-subtle text-success';
      }
      if (corLower.includes('warning') || corLower.includes('amarelo') || corLower.includes('laranja')) {
        return 'bg-warning-subtle text-warning';
      }
      if (corLower.includes('danger') || corLower.includes('vermelho')) {
        return 'bg-danger-subtle text-danger';
      }
      if (corLower.includes('info') || corLower.includes('azul')) {
        return 'bg-info-subtle text-info';
      }
      if (corLower.includes('primary')) {
        return 'bg-primary-subtle text-primary';
      }
    }
    
    // Se não encontrou cor, usar padrão secundário
    return 'bg-secondary-subtle text-secondary';
  }

  trackByContact(index: number, contact: QualifiedContactList): any {
    return contact.clienteId || index;
  }

  
  open(contactListClient: QualifiedContactList) {

    const offCanvasRef = this.offcanvasService.open(
      DetailedQualifiedListComponent,
      {
        position: "end",
        panelClass: "offcanvas-wide",
      },
    );
    offCanvasRef.componentInstance.client = contactListClient;
    offCanvasRef.closed.subscribe({
      next: () => {
        this.contactService.getQualifiedContactList(this.filtro!)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: GetQualifiedContactListResponseDTO) => {
            this.lists = response.qualificados;
            this.pagination = response.paginacao;
            this.isLoading = false;
          },
          error: (error) => {
            console.error(error);
          }
        })
      },
    });
  }

  getDetailedList(data: GetDetailedListRequestDTO) {

    this.isLoading = true;
    this.contactService
      .getDetailedList({...data, })
      .pipe(takeUntilDestroyed(this.destroyedRef))
      .subscribe({
        next: (response) => {
          this.rows = response.contatosGerados;
          this.pagination = response.paginacao;
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          this.error = "Erro ao buscar lista detalhada";
        },
      });
  }

  permissaoAdicionarUsuario(): void {
    this.podeEditar = this.authService.verificarPermissao('Call_Qualificados', 'Editar')
  }

  iniciarTour(): void {
    // Aguardar um pouco para garantir que o DOM está renderizado
    setTimeout(() => {
      // Verificar se pelo menos um elemento do tour existe
      const primeiroElemento = document.querySelector(qualifiedListTourSteps[0]?.element);
      
      if (!primeiroElemento) {
        console.warn('Elementos do tour não encontrados. Verifique se a página carregou completamente.');
        return;
      }

      // Importar e usar driver.js
      import('driver.js').then((driverModule) => {
        const { driver } = driverModule;
        
        if (!driver) {
          console.error('Driver.js não foi carregado corretamente.');
          return;
        }
        
        // Filtrar apenas os steps cujos elementos existem no DOM
        const stepsValidos = qualifiedListTourSteps.filter((step) => {
          const elemento = document.querySelector(step.element);
          return elemento !== null;
        });

        if (stepsValidos.length === 0) {
          console.warn('Nenhum elemento do tour está visível.');
          return;
        }

        // Configuração do driver
        const driverObj = driver({
          showProgress: true,
          steps: stepsValidos,
          allowClose: true,
          overlayOpacity: 0.75,
          stagePadding: 4,
          stageRadius: 5,
          smoothScroll: true,
          animate: true,
          onHighlightStarted: (element: any) => {
            // Garantir que o overlay seja criado e visível
            setTimeout(() => {
              const overlayEl = document.querySelector('.driver-overlay') as HTMLElement;
              if (overlayEl) {
                overlayEl.style.display = 'block';
                overlayEl.style.opacity = '0.75';
                overlayEl.style.zIndex = '9998';
              }
            }, 50);
          },
          onPopoverRender: (popover: any, options: any) => {
            // Traduzir botões para português
            setTimeout(() => {
              const popoverEl = document.querySelector('.driver-popover') as HTMLElement;
              if (popoverEl) {
                // Traduzir apenas os textos dos botões
                const buttons = popoverEl.querySelectorAll('button');
                buttons.forEach((btn) => {
                  const text = btn.textContent?.trim();
                  if (text === 'Next') {
                    btn.textContent = 'Próximo';
                  } else if (text === 'Previous') {
                    btn.textContent = 'Anterior';
                  }
                });
                
                // Se for o primeiro step (resumo geral), centralizar no meio da tela
                if (options?.state?.activeIndex === 0) {
                  // Forçar recálculo das dimensões
                  popoverEl.style.display = 'block';
                  popoverEl.style.visibility = 'visible';
                  
                  // Obter dimensões reais do popover
                  const popoverRect = popoverEl.getBoundingClientRect();
                  const windowWidth = window.innerWidth;
                  const windowHeight = window.innerHeight;
                  
                  // Calcular posição central
                  const left = Math.max(20, (windowWidth - popoverRect.width) / 2);
                  const top = Math.max(20, (windowHeight - popoverRect.height) / 2);
                  
                  // Aplicar posição centralizada com !important para sobrescrever estilos do Driver.js
                  popoverEl.style.setProperty('position', 'fixed', 'important');
                  popoverEl.style.setProperty('left', `${left}px`, 'important');
                  popoverEl.style.setProperty('top', `${top}px`, 'important');
                  popoverEl.style.setProperty('right', 'auto', 'important');
                  popoverEl.style.setProperty('bottom', 'auto', 'important');
                  popoverEl.style.setProperty('transform', 'none', 'important');
                  popoverEl.style.setProperty('margin', '0', 'important');
                }
              }
              
              // Garantir que o overlay esteja visível
              const overlayEl = document.querySelector('.driver-overlay') as HTMLElement;
              if (overlayEl) {
                overlayEl.style.display = 'block';
                overlayEl.style.opacity = '0.75';
                overlayEl.style.zIndex = '9998';
              }
            }, 150);
          }
        });

        // Iniciar o tour
        driverObj.drive();
      }).catch((error) => {
        console.error('Erro ao carregar Driver.js:', error);
      });
    }, 300);
  }
}
